/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import { FormattedMessage } from 'react-intl';

import { Box } from 'src/components/Box';
import { Button, ButtonGroup } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { Select } from 'src/components/Select';
import { SelectChangeParams } from 'src/components/Select/Select.types';
import { Text } from 'src/components/Text';
import { Viewport } from 'src/containers/Viewport';
import { Margins, Paddings, Responsive } from 'src/design-system/style-types';
import { messages } from 'src/messages';
import {
  getCurrentPageOptions,
  getItemsPerPageOptions,
  getOffset,
  getTotalPages,
} from 'src/utils/tables/pagination/utils';

export interface PageChangeData {
  page: number;
  itemsPerPage: number;
  offset: number;
  limit: number;
}

export interface Props {
  page: number;
  onPageChange: ({ page, itemsPerPage, offset }: PageChangeData) => void;
  totalItems: number;
  className?: string;
  itemsPerPage?: number;
  itemsPerPageOptions?: number[];
  itemsPerPageText?: React.ReactNode;
  showItemsPerPageSelect?: boolean;
  showPageSelect?: boolean;
  showPrevNextButtons?: boolean;
  marginTop?: Responsive<Margins>;
  marginBottom?: Responsive<Margins>;
  showControls?: boolean;
  p?: Responsive<Paddings>;
}

export const Pagination = ({
  className = '',
  itemsPerPage = 10,
  itemsPerPageOptions = [10, 20, 50, 100],
  itemsPerPageText = <FormattedMessage {...messages.show} />,
  marginTop = '0',
  marginBottom = '0',
  onPageChange,
  page,
  showControls,
  showItemsPerPageSelect = true,
  showPageSelect = true,
  showPrevNextButtons = true,
  totalItems,
  p = '0',
}: Props) => {
  const handleItemsPerPageSelectChange = ({ value }: SelectChangeParams) => {
    const page = 1;
    const itemsPerPage = Array.isArray(value) && value[0] && Number(value[0].value);

    if (!itemsPerPage) {
      return;
    }

    onPageChange({ page, itemsPerPage, offset: getOffset({ page, itemsPerPage }), limit: itemsPerPage });
  };

  const handlePageSelectChange = ({ value }: SelectChangeParams) => {
    const page = Array.isArray(value) && value[0] && Number(value[0].value);

    if (page) {
      onPageChange({ page, itemsPerPage, offset: getOffset({ page, itemsPerPage }), limit: itemsPerPage });
    }
  };

  const usedShowControls = (() => {
    if (typeof showControls === 'boolean') {
      return showControls;
    }

    const lowestValue = Math.min(itemsPerPageOptions?.[0], itemsPerPage);

    return totalItems > lowestValue;
  })();

  if (!usedShowControls) {
    return null;
  }

  const totalPages = getTotalPages({ totalItems, itemsPerPage });

  return (
    <Box j="space-between" a="center" mt={marginTop} mb={marginBottom} className={className} p={p}>
      {showItemsPerPageSelect ? (
        <Viewport tabletUp>
          <Box w={{ xs: 12, sm: 'auto' }} a="center" j="flex-end" mb={{ xs: 'lg', sm: '0' }} wrap="nowrap">
            {itemsPerPageText && (
              <Text size="sm" marginRight="md" data-qa="items-per-page-text" whiteSpace="nowrap">
                {itemsPerPageText}
              </Text>
            )}

            <Select
              data-qa="items-per-page-select"
              fontSize="sm"
              name="items-per-page-select"
              value={[{ label: itemsPerPage, value: itemsPerPage }]}
              options={getItemsPerPageOptions({ itemsPerPageOptions })}
              onChange={handleItemsPerPageSelectChange}
              variant="filter"
            />
          </Box>
        </Viewport>
      ) : (
        <div></div>
      )}

      {(showPageSelect || showPrevNextButtons) && (
        <Box w={{ xs: 12, sm: 'auto' }} a="center" j={{ xs: 'space-between', sm: 'flex-start' }}>
          <Box w="auto" a="center" wrap="nowrap">
            <Text size="sm" marginRight="sm">
              <FormattedMessage id="pagination.page" defaultMessage="Page" />
            </Text>

            {showPageSelect ? (
              <Select
                data-qa="page-select"
                fontSize="sm"
                name="pagination-select"
                value={[{ label: page, value: page }]}
                options={getCurrentPageOptions({ totalItems, itemsPerPage })}
                onChange={handlePageSelectChange}
                variant="filter"
              />
            ) : (
              <Text size="sm">{page}</Text>
            )}

            <Viewport tabletUp>
              <Text size="sm" marginLeft="sm" whiteSpace="nowrap">
                <FormattedMessage
                  id="pagination.items_per_page"
                  defaultMessage="of {totalPages}"
                  values={{
                    totalPages,
                  }}
                />
              </Text>
            </Viewport>
          </Box>

          {showPrevNextButtons && (
            <Box w="auto" a="center" ml="2xl">
              <ButtonGroup>
                <Button
                  data-qa="prev-page"
                  variant="secondary"
                  size="small"
                  disabled={page === 1}
                  icon={<Icon enableRtl name="left" hasHoverClass />}
                  onClick={(event) => {
                    event?.preventDefault();
                    const prevPage = page - 1;
                    onPageChange({
                      page: prevPage,
                      itemsPerPage,
                      offset: getOffset({ page: prevPage, itemsPerPage }),
                      limit: itemsPerPage,
                    });
                  }}
                />
                <Button
                  data-qa="next-page"
                  variant="secondary"
                  size="small"
                  disabled={page === totalPages}
                  icon={<Icon enableRtl name="right" hasHoverClass />}
                  type="button"
                  onClick={(event) => {
                    event?.preventDefault();
                    const nextPage = page + 1;
                    onPageChange({
                      page: nextPage,
                      itemsPerPage,
                      offset: getOffset({ page: nextPage, itemsPerPage }),
                      limit: itemsPerPage,
                    });
                  }}
                />
              </ButtonGroup>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
